/**
 * One event stream per app, however many components listen to it.
 *
 * `useConsentRequests` is mounted by the notification bell, the consent
 * overlay, the consent modal and the consent page - so a dashboard opened four
 * long-lived connections to the backend, each held open across three replicas,
 * to receive the same events four times. That was true of the EventSource
 * version too; it only became visible once the stream started working.
 *
 * Subscribers are counted. The first one opens the connection, and the last one
 * to leave closes it - on the next tick, not at once. React runs every effect
 * cleanup in a commit before any new effect, so a re-render that resubscribes
 * all four listeners briefly drops the count to zero. Closing there aborted a
 * healthy stream only to reopen it a moment later.
 */

import { openEventStream } from "@/lib/eventStream";

type Listener = (type: string, data: string) => void;

const listeners = new Set<Listener>();
let close: (() => void) | null = null;
let pendingClose: ReturnType<typeof setTimeout> | null = null;

export function subscribeToEventStream(
  url: string,
  listener: Listener,
): () => void {
  listeners.add(listener);

  if (pendingClose) {
    clearTimeout(pendingClose);
    pendingClose = null;
  }

  if (!close) {
    close = openEventStream(url, {
      onEvent: (type, data) => {
        // A copy, because a listener may unsubscribe while being called.
        [...listeners].forEach((notify) => notify(type, data));
      },
    });
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && close && !pendingClose) {
      pendingClose = setTimeout(() => {
        pendingClose = null;
        if (listeners.size === 0 && close) {
          close();
          close = null;
        }
      }, 0);
    }
  };
}
